import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import Spinner from './Spinner';
import Toast from './Toast';

function AdminPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [status, setStatus] = useState({});

  const [proxies, setProxies] = useState([]);
  const [serviceEnabled, setServiceEnabled] = useState(false);
  const [newHost, setNewHost] = useState('');
  const [newPort, setNewPort] = useState('3128');
  const [newProxyUser, setNewProxyUser] = useState('');
  const [newProxyPass, setNewProxyPass] = useState('');

  const [newUser, setNewUser] = useState({ username: '', password: '', companyName: '', smsPackage: 'mini' });
  const [createStatus, setCreateStatus] = useState('');

  const [dashboard, setDashboard] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userSms, setUserSms] = useState([]);
  const [toast, setToast] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [expandedLogins, setExpandedLogins] = useState(null);
  const [userLogins, setUserLogins] = useState([]);

  const showError = (e, fallback) => setToast({ type: 'error', message: (e && e.response && e.response.data && e.response.data.error) || fallback });
  const showOk = (message) => setToast({ type: 'success', message });

  const loadDashboard = useCallback(async () => {
    try {
      const res = await apiService.getDashboard();
      setDashboard(res.data);
    } catch (e) { /* handled by forbidden */ }
  }, []);

  const viewSms = async (username) => {
    if (expandedUser === username) { setExpandedUser(null); return; }
    setExpandedUser(username);
    setUserSms([]);
    try {
      const res = await apiService.getDealershipSms(username);
      setUserSms(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const viewLogins = async (id) => {
    if (expandedLogins === id) { setExpandedLogins(null); return; }
    setExpandedLogins(id);
    setUserLogins([]);
    try {
      const res = await apiService.getUserLogins(id);
      setUserLogins(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const loadProxies = useCallback(async () => {
    try {
      const res = await apiService.getProxies();
      setProxies(res.data.proxies || []);
      setServiceEnabled(!!res.data.serviceEnabled);
    } catch (e) { /* handled by forbidden */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminUsers();
      setUsers(res.data || []);
      await loadProxies();
      await loadDashboard();
    } catch (e) {
      if (e.response && e.response.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, [loadProxies, loadDashboard]);

  useEffect(() => { load(); }, [load]);

  const addProxy = async () => {
    if (!newHost.trim()) return;
    await apiService.addProxy(newHost.trim(), parseInt(newPort, 10) || 3128, newProxyUser.trim(), newProxyPass);
    setNewHost('');
    setNewPort('3128');
    setNewProxyUser('');
    setNewProxyPass('');
    loadProxies();
  };
  const removeProxy = async (id) => {
    if (!window.confirm('Odstraniti ta proxy?')) return;
    await apiService.deleteProxy(id);
    loadProxies();
  };
  const toggleProxy = async (id) => {
    await apiService.toggleProxy(id);
    loadProxies();
  };
  const toggleService = async () => {
    await apiService.setProxyService(!serviceEnabled);
    loadProxies();
  };

  const createUser = async () => {
    setCreateStatus('Ustvarjam ...');
    try {
      await apiService.createUser(newUser);
      setCreateStatus('');
      showOk('Avtohiša ustvarjena');
      setNewUser({ username: '', password: '', companyName: '', smsPackage: 'mini' });
      load();
    } catch (e) {
      setCreateStatus('');
      showError(e, 'Napaka pri ustvarjanju');
    }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`Trajno izbrisati avtohišo "${username}"? Tega ni mogoče razveljaviti.`)) return;
    try {
      await apiService.deleteUser(id);
      setUsers((list) => list.filter((u) => u.id !== id));
      showOk('Avtohiša izbrisana');
    } catch (e) {
      showError(e, 'Napaka pri brisanju');
    }
  };

  const resetPassword = async (id, username) => {
    const pwd = window.prompt(`Novo geslo za "${username}" (min. 6 znakov):`);
    if (pwd == null) return;
    try {
      await apiService.resetUserPassword(id, pwd);
      showOk('Geslo ponastavljeno');
    } catch (e) {
      showError(e, 'Napaka pri ponastavitvi gesla');
    }
  };

  const toggleActive = async (id, current) => {
    setStatus((s) => ({ ...s, [id]: 'Shranjujem ...' }));
    try {
      await apiService.setUserActive(id, !current);
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, active: !current } : u)));
      setStatus((s) => ({ ...s, [id]: '✓' }));
    } catch (e) {
      setStatus((s) => ({ ...s, [id]: '✗ Napaka' }));
    }
    setTimeout(() => setStatus((s) => ({ ...s, [id]: '' })), 2500);
  };

  const changePackage = async (id, smsPackage) => {
    setStatus((s) => ({ ...s, [id]: 'Shranjujem ...' }));
    try {
      await apiService.setUserPackage(id, smsPackage);
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, smsPackage } : u)));
      setStatus((s) => ({ ...s, [id]: '✓ Shranjeno' }));
    } catch (e) {
      setStatus((s) => ({ ...s, [id]: '✗ Napaka' }));
    }
    setTimeout(() => setStatus((s) => ({ ...s, [id]: '' })), 2500);
  };

  const changeGebrauchtwagen = async (id, current) => {
    setStatus((s) => ({ ...s, [id]: 'Shranjujem ...' }));
    try {
      await apiService.setUserGebrauchtwagen(id, !current);
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, gebrauchtwagenEnabled: !current } : u)));
      setStatus((s) => ({ ...s, [id]: '✓ Shranjeno' }));
    } catch (e) {
      setStatus((s) => ({ ...s, [id]: '✗ Napaka' }));
    }
    setTimeout(() => setStatus((s) => ({ ...s, [id]: '' })), 2500);
  };

  const changeUserType = async (id, userType) => {
    setStatus((s) => ({ ...s, [id]: 'Shranjujem ...' }));
    try {
      await apiService.setUserType(id, userType);
      setUsers((list) => list.map((u) => (u.id === id ? { ...u, userType } : u)));
      setStatus((s) => ({ ...s, [id]: '✓ Shranjeno' }));
    } catch (e) {
      setStatus((s) => ({ ...s, [id]: '✗ Napaka' }));
    }
    setTimeout(() => setStatus((s) => ({ ...s, [id]: '' })), 2500);
  };

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px' }}>
        {!forbidden && (!user || user.role === 'ADMIN') && dashboard ? (
          <div style={{ marginBottom: 28 }}>
            <h2>Nadzorna plošča</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                ['Avtohiše', `${dashboard.activeDealerships}/${dashboard.dealerships}`, 'aktivne/skupaj'],
                ['SMS ta mesec', dashboard.smsThisMonth, 'vse avtohiše'],
                ['Proxyji', `${dashboard.proxyReachable}/${dashboard.proxyTotal}`, 'dosegljivi/skupaj'],
              ].map((c) => (
                <div key={c[0]} style={{ flex: '1 1 200px', border: '1px solid #ddd', borderRadius: 8, padding: 14, background: 'white' }}>
                  <div style={{ fontSize: 13, color: '#555' }}>{c[0]}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#007AFF' }}>{c[1]}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>{c[2]}</div>
                </div>
              ))}
            </div>

            <div style={{ fontWeight: 600, marginBottom: 6 }}>Evidenca pošiljanja po avtohišah</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555' }}>
                  <th style={{ padding: 6 }}>Avtohiša</th>
                  <th style={{ padding: 6 }}>Paket</th>
                  <th style={{ padding: 6 }}>SMS ta mesec</th>
                  <th style={{ padding: 6 }}>SMS skupaj</th>
                  <th style={{ padding: 6 }}></th>
                </tr>
              </thead>
              <tbody>
                {(dashboard.dealershipRows || []).map((r) => (
                  <React.Fragment key={r.username}>
                    <tr style={{ borderBottom: '1px solid #f0f0f0', opacity: r.active ? 1 : 0.5 }}>
                      <td style={{ padding: 6 }}>{r.companyName || r.username}</td>
                      <td style={{ padding: 6 }}>{r.smsPackage || '—'}</td>
                      <td style={{ padding: 6 }}>{r.smsThisMonth}</td>
                      <td style={{ padding: 6 }}>{r.smsTotal}</td>
                      <td style={{ padding: 6 }}>
                        <button onClick={() => viewSms(r.username)} style={{ padding: '3px 10px' }}>
                          {expandedUser === r.username ? 'Skrij' : 'Kaj pošilja'}
                        </button>
                      </td>
                    </tr>
                    {expandedUser === r.username ? (
                      <tr>
                        <td colSpan={5} style={{ padding: 8, background: '#fafafa' }}>
                          {userSms.length === 0 ? <span style={{ color: '#888' }}>Ni pošiljanj.</span> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ textAlign: 'left', color: '#777' }}>
                                  <th style={{ padding: 4 }}>Čas</th>
                                  <th style={{ padding: 4 }}>Prejemnik</th>
                                  <th style={{ padding: 4 }}>Sporočilo</th>
                                  <th style={{ padding: 4 }}>Način / filter</th>
                                  <th style={{ padding: 4 }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userSms.map((m, i) => (
                                  <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                                    <td style={{ padding: 4, whiteSpace: 'nowrap' }}>{m.sentAt ? new Date(m.sentAt).toLocaleString('sl-SI') : ''}</td>
                                    <td style={{ padding: 4 }}>{m.recipient}</td>
                                    <td style={{ padding: 4, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message}</td>
                                    <td style={{ padding: 4, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>{m.filterUsed || '—'}</td>
                                    <td style={{ padding: 4 }}>{m.sendStatus}{m.deliveryStatus ? ` / ${m.deliveryStatus}` : ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <h2>Administracija – paketi uporabnikov</h2>

        {!forbidden && (!user || user.role === 'ADMIN') ? (
          <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20, background: '#fafafa' }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Dodaj novo avtohišo</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Uporabniško ime
                <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} placeholder="npr. avtohisa_novak" style={{ padding: 7, minWidth: 170 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Geslo (min. 6)
                <input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="geslo" style={{ padding: 7, minWidth: 140 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Ime avtohiše
                <input value={newUser.companyName} maxLength={50} onChange={(e) => setNewUser({ ...newUser, companyName: e.target.value })} placeholder="Avtohiša Novak" style={{ padding: 7, minWidth: 180 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>Paket
                <select value={newUser.smsPackage} onChange={(e) => setNewUser({ ...newUser, smsPackage: e.target.value })} style={{ padding: 7 }}>
                  <option value="">— brez —</option>
                  <option value="test">Test (10 SMS)</option>
                  <option value="mini">Mini (500 SMS)</option>
                  <option value="maxi">Maxi (1200 SMS)</option>
                </select>
              </label>
              <button onClick={createUser} disabled={!newUser.username || !newUser.password} style={{ padding: '8px 16px' }}>Ustvari avtohišo</button>
              <span style={{ color: createStatus.startsWith('✗') ? '#d00' : '#007AFF' }}>{createStatus}</span>
            </div>
          </div>
        ) : null}

        {user && user.role !== 'ADMIN' ? (
          <p style={{ color: '#d00' }}>Nimaš administratorskih pravic.</p>
        ) : forbidden ? (
          <p style={{ color: '#d00' }}>Dostop zavrnjen (potrebne so administratorske pravice).</p>
        ) : loading ? (
          <Spinner />
        ) : users.length === 0 ? (
          <p>Ni uporabnikov.</p>
        ) : (
          <>
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Išči avtohišo (ime/uporabnik/telefon)…"
            style={{ padding: 7, minWidth: 280, marginBottom: 10 }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: 8 }}>Uporabnik</th>
                <th style={{ padding: 8 }}>Avtohiša</th>
                <th style={{ padding: 8 }}>Telefon</th>
                <th style={{ padding: 8 }}>Paket</th>
                <th style={{ padding: 8 }}>Tip</th>
                <th style={{ padding: 8 }}>Stanje</th>
                <th style={{ padding: 8 }}>Dejanja</th>
                <th style={{ padding: 8 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.filter((u) => {
                const q = userSearch.trim().toLowerCase();
                return !q || `${u.username} ${u.companyName || ''} ${u.contactPhone || ''}`.toLowerCase().includes(q);
              }).map((u) => (
                <React.Fragment key={u.id}>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{u.username}</td>
                  <td style={{ padding: 8 }}>{u.companyName || '-'}</td>
                  <td style={{ padding: 8 }}>{u.contactPhone || '-'}</td>
                  <td style={{ padding: 8 }}>
                    <select value={u.smsPackage || ''} onChange={(e) => changePackage(u.id, e.target.value)} style={{ padding: 6 }}>
                      <option value="">— brez —</option>
                      <option value="test">Test (10 SMS)</option>
                      <option value="mini">Mini (500 SMS)</option>
                      <option value="maxi">Maxi (1200 SMS)</option>
                    </select>
                  </td>
                  <td style={{ padding: 8 }}>
                    <select value={u.userType || 'navadni'} onChange={(e) => changeUserType(u.id, e.target.value)} style={{ padding: 6 }}>
                      <option value="navadni">Navadni</option>
                      <option value="rabljeni">Rabljeni (V okvari)</option>
                      <option value="novi">Novi (1. lastnik)</option>
                    </select>
                  </td>
                  <td style={{ padding: 8, color: u.active ? '#080' : '#d00' }}>{u.active ? 'aktivna' : 'neaktivna'}</td>
                  <td style={{ padding: 8 }}>
                    <button onClick={() => toggleActive(u.id, u.active)} style={{ padding: '4px 10px', marginRight: 6 }}>
                      {u.active ? 'Deaktiviraj' : 'Aktiviraj'}
                    </button>
                    <button onClick={() => changeGebrauchtwagen(u.id, u.gebrauchtwagenEnabled)} title="Vpogled v zavihek Gebrauchtwagen (AT)" style={{ padding: '4px 10px', marginRight: 6, border: 'none', borderRadius: 4, background: u.gebrauchtwagenEnabled ? '#0a0' : '#eee', color: u.gebrauchtwagenEnabled ? '#fff' : '#333' }}>
                      GW {u.gebrauchtwagenEnabled ? 'ON' : 'OFF'}
                    </button>
                    <button onClick={() => resetPassword(u.id, u.username)} style={{ padding: '4px 10px', marginRight: 6 }}>Geslo</button>
                    <button onClick={() => viewLogins(u.id)} style={{ padding: '4px 10px', marginRight: 6 }}>
                      {expandedLogins === u.id ? 'Skrij prijave' : 'Prijave'}
                    </button>
                    <button onClick={() => deleteUser(u.id, u.username)} style={{ padding: '4px 10px', color: '#d00' }}>Briši</button>
                  </td>
                  <td style={{ padding: 8, color: '#007AFF' }}>{status[u.id]}</td>
                </tr>
                {expandedLogins === u.id ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 8, background: '#fafafa' }}>
                      {userLogins.length === 0 ? <span style={{ color: '#888' }}>Ni zabeleženih prijav.</span> : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', color: '#777' }}>
                              <th style={{ padding: 4 }}>Datum in ura prijave</th>
                              <th style={{ padding: 4 }}>Aplikacija</th>
                              <th style={{ padding: 4 }}>IP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userLogins.map((l, i) => (
                              <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                                <td style={{ padding: 4, whiteSpace: 'nowrap' }}>{l.loginTime ? new Date(l.loginTime).toLocaleString('sl-SI') : ''}</td>
                                <td style={{ padding: 4 }}>{l.applicationType === 'MOBILE' ? 'Mobilna' : 'Splet'}</td>
                                <td style={{ padding: 4, color: '#888' }}>{l.ipAddress || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </>
        )}

        {!forbidden && !loading && (!user || user.role === 'ADMIN') ? (
          <div style={{ marginTop: 40 }}>
            <h2>Proxy strežniki (Squid rotator)</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontWeight: 600 }}>Proxy storitev:</span>
              <button onClick={toggleService} style={{ padding: '8px 16px', background: serviceEnabled ? '#080' : '#999', color: 'white', border: 'none', borderRadius: 6 }}>
                {serviceEnabled ? 'VKLOPLJENA' : 'IZKLOPLJENA'}
              </button>
              <span style={{ color: '#888', fontSize: 13 }}>(klik za preklop)</span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: 8 }}>IP / host</th>
                  <th style={{ padding: 8 }}>Port</th>
                  <th style={{ padding: 8 }}>Uporabnik</th>
                  <th style={{ padding: 8 }}>Stanje</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {proxies.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>{p.host}</td>
                    <td style={{ padding: 8 }}>{p.port}</td>
                    <td style={{ padding: 8 }}>{p.username ? `${p.username} 🔒` : '—'}</td>
                    <td style={{ padding: 8 }}>
                      <button onClick={() => toggleProxy(p.id)} style={{ padding: '4px 10px', background: p.enabled ? '#080' : '#999', color: 'white', border: 'none', borderRadius: 4 }}>
                        {p.enabled ? 'aktiven' : 'neaktiven'}
                      </button>
                    </td>
                    <td style={{ padding: 8 }}>
                      <button onClick={() => removeProxy(p.id)} style={{ padding: '4px 10px', color: '#d00' }}>Odstrani</button>
                    </td>
                  </tr>
                ))}
                {proxies.length === 0 ? <tr><td colSpan={5} style={{ padding: 8, color: '#888' }}>Ni proxy strežnikov.</td></tr> : null}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={newHost} onChange={(e) => setNewHost(e.target.value)} placeholder="IP / host (npr. 77.111.9.187)" style={{ padding: 7, minWidth: 220 }} />
              <input value={newPort} onChange={(e) => setNewPort(e.target.value)} placeholder="port" style={{ padding: 7, width: 80 }} />
              <input value={newProxyUser} onChange={(e) => setNewProxyUser(e.target.value)} placeholder="uporabnik (če zahteva)" style={{ padding: 7, minWidth: 150 }} />
              <input type="password" value={newProxyPass} onChange={(e) => setNewProxyPass(e.target.value)} placeholder="geslo (če zahteva)" style={{ padding: 7, minWidth: 150 }} />
              <button onClick={addProxy} style={{ padding: '8px 16px' }}>Dodaj proxy</button>
            </div>
          </div>
        ) : null}
      </div>
      <Toast message={toast && toast.message} type={toast && toast.type} onClose={() => setToast(null)} />
    </div>
  );
}

export default AdminPanel;
